/* eslint-disable style/quote-props */
import type { SnackBarConfig } from '../Snackbar';
import type { CompanyOption } from './FollowCompanyModal.types';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { CreateCompanyModal } from './CreateCompanyModal';
import { UserContext } from '@/shared/contexts/UserContext';
import { useResponsive } from '../../hooks/useResponsive';
import { useApi } from '../../services/api';
import { colors } from '../../theme';
import CompanyLogo from '../CompanyLogo';
import CompanySearchAutocomplete from '../CompanySearch';
import CustomSnackbar from '../Snackbar';
import FollowCompanyMobileModal from './FollowCompanyMobileModal';
import {
  LogoWrapper,
} from './FollowCompanyModal.styles';

// Reusable component for individual company items
interface CompanyListItemProps {
  company: CompanyOption;
  onClick: () => void;
}

function CompanyListItem({ company, onClick }: CompanyListItemProps) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        m: '4px 0',
        cursor: 'pointer',
        p: 1,
        transition: 'background-color 0.2s, transform 0.2s',
        opacity: 0,
        transform: 'translateX(-10px)',
        animation: 'slideIn 0.3s ease forwards',
        '@keyframes slideIn': {
          to: {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
        '&:hover': {
          backgroundColor: '#eef6ff',
          transform: 'translateX(4px)',
        },
      }}
    >
      <LogoWrapper small>
        <CompanyLogo
          companyName={company?.label ?? ''}
          companyId={company?.id}
          sx={{ width: '100%', height: '100%' }}
          alt={company?.label ?? ''}
        />
      </LogoWrapper>
      <ListItemText primary={company.label} />
    </Box>
  );
}

// Reusable component for empty states
interface EmptyStateProps {
  emptyMessage?: string;
}

function EmptyState({
  emptyMessage,
}: EmptyStateProps) {
  if (emptyMessage) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          textAlign: 'center',
          p: 4,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: '#666',
            fontStyle: 'italic',
          }}
        >
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return null;
}

// Main reusable component for company lists
interface CompanyListProps {
  title: string;
  companies: CompanyOption[];
  onCompanyClick: (company: CompanyOption) => void;
  emptyMessage?: string;
  className?: string;
}

function CompanyList({
  title,
  companies,
  onCompanyClick,
  emptyMessage,
  className,
}: CompanyListProps) {
  return (
    <Box
      className={className}
      sx={{
        width: '100%',
        maxWidth: '500px',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <Typography
        variant="subtitle2"
        align="center"
        sx={{ mb: 1, fontWeight: 'bold', flexShrink: 0 }}
      >
        {title}
      </Typography>
      <Box sx={{
        flex: 1,
        minHeight: 0,
        border: `2px solid ${colors.grey}`,
        borderRadius: '8px',
        overflowY: 'auto',
        px: 1,
        bgcolor: colors.white,
      }}
      >
        <List dense component="div" role="list">
          {companies.length > 0
            ? (
                companies.map(company => (
                  <CompanyListItem
                    key={company.id}
                    company={company}
                    onClick={() => onCompanyClick(company)}
                  />
                ))
              )
            : (
                <EmptyState
                  emptyMessage={emptyMessage}
                />
              )}
          <ListItem />
        </List>
      </Box>
    </Box>
  );
}

function FollowCompanyModal() {
  const { user, refetchUser } = useContext(UserContext);
  const api = useApi();
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>({
    open: false,
    message: '',
    type: 'success',
  });
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(
    null,
  );
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);

  const { isMobile } = useResponsive();

  const followedCompanies = user?.followedCompanies || [];

  const handleSnackBarClose = useCallback(() => {
    setSnackbarConfig(prev => ({ ...prev, open: false }));
  }, []);

  const chosen = useMemo<CompanyOption[]>(() => {
    return followedCompanies
      .filter(company => company.id != null && company.companyName != null)
      .map(company => ({
        id: company.id!,
        label: company.companyName!,
        logo: `https://kandir-images.s3.eu-west-2.amazonaws.com/Company Logos/${encodeURIComponent(company.companyName!)}.jpeg`,
      }));
  }, [followedCompanies]);

  const handleFollow = async (company: CompanyOption) => {
    if (!user)
      return;
    await api.post(
      `users/follow-company`,
      {
        companyId: company.id,
        userId: user.id,
      },
    );
    refetchUser();
    setSelectedCompany(null);
    setSnackbarConfig({
      open: true,
      message: `You're now following ${company.label}.`,
      type: 'success',
    });
  };

  const handleUnfollow = async (companyId: number) => {
    if (!user)
      return;
    await api.post(
      `users/unfollow-company`,
      {
        companyId,
        userId: user.id,
      },
    );
    refetchUser();
    setSnackbarConfig({ open: true, message: `Unfollowed.`, type: 'info' });
  };
  return (
    <Box
      className="follow-company-modal"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '80vh',
        overflow: 'hidden',
      }}
    >
      {isMobile
        ? (
            <FollowCompanyMobileModal
              selectedCompany={selectedCompany}
              setSelectedCompany={setSelectedCompany}
              followedCompanyChips={followedCompanies}
              handleFollowCompany={handleFollow}
              handleUnfollowCompany={handleUnfollow}
              snackbarConfig={snackbarConfig}
              handleSnackBarClose={handleSnackBarClose}
              onAddCompany={() => setShowAddCompanyModal(true)}
            />
          )
        : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                maxHeight: '70vh', // Prevent modal from getting too tall
                // minHeight: '500px', // Ensure minimum usable height
              }}
            >
              <Typography variant="body2" mb={2} sx={{ flexShrink: 0 }}>
                Select a company to follow. You'll receive email notifications when
                new reviews or intelligence requests are posted for that company.
              </Typography>

              <Box className="follow-company-modal-autocomplete" sx={{ flexShrink: 0, mb: 2 }}>
                <CompanySearchAutocomplete
                  value={selectedCompany}
                  onChange={(company) => {
                    if (company?.id) {
                      handleFollow(company);
                    }
                    else {
                      setSelectedCompany(company);
                    }
                  }}
                  label="Company Search"
                  placeholder="Find a company to follow"
                  helperText="Search for a company to follow and get notifications"
                  minSearchLength={2}
                  allowNewCompany={false}
                />
              </Box>

              <Box
                className="follow-company-modal-lists"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'stretch',
                  flex: 1,
                  minHeight: 0,
                  pb: 1,
                }}
              >
                <CompanyList
                  title="Following"
                  companies={chosen}
                  onCompanyClick={company => handleUnfollow(company.id!)}
                  emptyMessage="You're not following any companies yet. Start adding some!"
                  className="follow-company-modal-following"
                />
              </Box>
            </Box>
          )}
      <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
      <CreateCompanyModal
        open={showAddCompanyModal}
        onClose={() => setShowAddCompanyModal(false)}
        onCompanyCreated={(company) => {
          setSnackbarConfig({
            open: true,
            type: 'success',
            message: `${company.companyName} added successfully!`,
          });
          setShowAddCompanyModal(false);
          refetchUser();
        }}
      />
    </Box>
  );
}

export default FollowCompanyModal;
